import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";

// The square cut: on phones, and beside the headline on wide screens. Tablets get 16:9 under the text.
// In a single column (e.g. /try) only phones get the square cut.
const SQUARE = { hero: "(max-width: 639px), (min-width: 1024px)", column: "(max-width: 639px)" };
const ASPECT = { hero: "aspect-square sm:aspect-video lg:aspect-square", column: "aspect-square sm:aspect-video" };

/**
 * The product film (made in ../intakall-video): muted and looping, like Otter's hero.
 * A visitor who prefers reduced motion gets the controls and no autoplay.
 */
export function HeroFilm({ layout = "hero" }: { layout?: "hero" | "column" }) {
  const film = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [square, setSquare] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    setReduceMotion(reduce);
    setSquare(window.matchMedia?.(SQUARE[layout]).matches ?? false);
    if (reduce) film.current?.pause?.();
  }, [layout]);

  function toggleSound() {
    const video = film.current;
    if (!video) return;
    video.muted = !muted;
    setMuted(!muted);
    // Turning the sound on should also start the film, e.g. when it was not autoplaying.
    if (!video.muted) video.play?.()?.catch?.(() => {});
  }

  return (
    <div className="relative overflow-hidden rounded-[20px] border border-char shadow-offset">
      <video
        ref={film}
        aria-label="Intakall product film"
        className={`block w-full bg-paper ${ASPECT[layout]}`}
        poster={square ? "/videos/intakall-film-square-poster.jpg" : "/videos/intakall-film-poster.jpg"}
        autoPlay={!reduceMotion}
        controls={reduceMotion}
        muted
        loop
        playsInline
        preload="metadata"
      >
        <source src="/videos/intakall-film-square.mp4" type="video/mp4" media={SQUARE[layout]} />
        <source src="/videos/intakall-film.mp4" type="video/mp4" />
      </video>
      {!reduceMotion && (
        <button
          type="button"
          onClick={toggleSound}
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
          className={`absolute bottom-3 right-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-char bg-paper/90 text-char shadow-[-2px_3px_0_0_#262524] transition-transform hover:-translate-x-px hover:translate-y-px ${focusRing}`}
        >
          {muted ? <VolumeX className="h-4 w-4" aria-hidden="true" /> : <Volume2 className="h-4 w-4" aria-hidden="true" />}
        </button>
      )}
    </div>
  );
}
